import { withStyleNonce } from '@/lib/csp-nonce';
import { useMemo } from 'react';
import { ArrowsClockwise, CalendarPlus, PaperPlaneTilt, UsersThree } from '@phosphor-icons/react';
import { format, parse, startOfDay } from 'date-fns';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';
import { CAMPAIGN_TIMEZONES, formatTimezoneLabel } from '../../constants/timezones.constants';

export type CampaignScheduleSendType = 'Regular' | 'Scheduled' | 'Recurring';
export type CampaignScheduleFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

type CampaignScheduleStepProps = {
  sendType: CampaignScheduleSendType;
  onSendTypeChange: (value: CampaignScheduleSendType) => void;
  scheduleDate: Date;
  scheduleTime: string;
  onScheduleDateTimeChange: (date: Date, time: string) => void;
  timeZone: string;
  onTimeZoneChange?: (timeZone: string) => void;
  recurringFrequency: CampaignScheduleFrequency;
  onRecurringFrequencyChange: (value: CampaignScheduleFrequency) => void;
  recurringStartDate: Date;
  onRecurringStartDateChange: (date: Date) => void;
  scheduleError?: string;
  audiencePreview?: {
    audienceCount: number;
    sendableCount: number;
    listNames: string[];
    segmentNames: string[];
  };
  contentPreview?: {
    html: string;
    label: string;
  };
};

const SEND_TYPE_CARDS: ReadonlyArray<{
  value: CampaignScheduleSendType;
  label: string;
  help: string;
  Icon: typeof PaperPlaneTilt;
}> = [
  {
    value: 'Regular',
    label: 'Send now',
    help: 'Confirmed on the next step.',
    Icon: PaperPlaneTilt,
  },
  {
    value: 'Scheduled',
    label: 'Schedule for later',
    help: 'Pick a date and time.',
    Icon: CalendarPlus,
  },
  {
    value: 'Recurring',
    label: 'Recurring',
    help: 'Repeats on a schedule.',
    Icon: ArrowsClockwise,
  },
];

const RECURRING_OPTIONS: ReadonlyArray<{ value: CampaignScheduleFrequency; label: string }> = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Yearly', label: 'Yearly' },
];

const PREVIEW_SRCDOC_STYLE =
  'body{margin:0;font-family:Inter,system-ui,sans-serif;font-size:13px;color:#27272C;padding:12px}' +
  'img{max-width:100%}h1{font-size:18px;margin:0 0 8px}p{margin:0 0 6px;line-height:1.5}';

const toPreviewSrcDoc = (html: string): string => {
  const body = html.trim();
  if (!body) return '';
  if (body.includes('<html')) return body;
  return `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_SRCDOC_STYLE}</style></head><body>${body}</body></html>`;
};

const pad2 = (value: number) => String(value).padStart(2, '0');

const toDatetimeLocalValue = (date: Date, time: string): string => {
  const parsed = parse(time || '00:00', 'HH:mm', startOfDay(date));
  if (Number.isNaN(parsed.getTime())) {
    return `${format(date, 'yyyy-MM-dd')}T00:00`;
  }
  return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}T${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`;
};

const fromDatetimeLocalValue = (value: string): { date: Date; time: string } | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    date: startOfDay(parsed),
    time: format(parsed, 'HH:mm'),
  };
};

const minDatetimeLocalValue = (): string => {
  const d = new Date(Date.now() + 6 * 60_000);
  d.setSeconds(0, 0);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const formatPreviewNames = (names: string[], emptyLabel: string): string => {
  if (names.length === 0) return emptyLabel;
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
};

export const CampaignScheduleStep = ({
  sendType,
  onSendTypeChange,
  scheduleDate,
  scheduleTime,
  onScheduleDateTimeChange,
  timeZone,
  onTimeZoneChange,
  recurringFrequency,
  onRecurringFrequencyChange,
  recurringStartDate,
  onRecurringStartDateChange,
  scheduleError,
  audiencePreview,
  contentPreview,
}: CampaignScheduleStepProps) => {
  const timezoneHelp = `Times are in ${formatTimezoneLabel(timeZone)}.`;
  const timezoneSelect = onTimeZoneChange ? (
    <div className="space-y-1.5">
      <Label htmlFor="schedule-timezone">Timezone</Label>
      <Select value={timeZone} onValueChange={onTimeZoneChange}>
        <SelectTrigger id="schedule-timezone" className="h-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {(CAMPAIGN_TIMEZONES.includes(timeZone)
            ? CAMPAIGN_TIMEZONES
            : [timeZone, ...CAMPAIGN_TIMEZONES]
          ).map((zone) => (
            <SelectItem key={zone} value={zone}>
              {formatTimezoneLabel(zone)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;
  const scheduledLocal = useMemo(
    () => toDatetimeLocalValue(scheduleDate, scheduleTime),
    [scheduleDate, scheduleTime]
  );
  const recurringLocal = useMemo(
    () => toDatetimeLocalValue(recurringStartDate, scheduleTime),
    [recurringStartDate, scheduleTime]
  );

  const recurrenceSummary = useMemo(() => {
    if (sendType !== 'Recurring') return '';
    const start = parse(scheduleTime || '00:00', 'HH:mm', startOfDay(recurringStartDate));
    const freq =
      RECURRING_OPTIONS.find((option) => option.value === recurringFrequency)?.label ??
      recurringFrequency;
    return `${freq}, starting ${format(start, 'MMM d, yyyy · h:mm a')} (${formatTimezoneLabel(timeZone)}) · one fixed clock time for everyone, not per-recipient.`;
  }, [sendType, recurringFrequency, recurringStartDate, scheduleTime, timeZone]);

  const contentSrcDoc = useMemo(
    () => toPreviewSrcDoc(contentPreview?.html ?? ''),
    [contentPreview?.html]
  );

  return (
    <div className="mx-auto flex w-full flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-e1 md:gap-5 md:p-6">
      <div className="grid flex-none gap-3 sm:grid-cols-3">
        {SEND_TYPE_CARDS.map(({ value, label, help, Icon }) => {
          const selected = sendType === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => onSendTypeChange(value)}
              className={cn(
                'rounded-lg px-4 py-3 text-left transition-colors',
                selected
                  ? 'border-2 border-primary-600 bg-primary-50 shadow-sm ring-2 ring-primary-600/20'
                  : 'border border-neutral-300 bg-white hover:border-neutral-400 hover:bg-neutral-50'
              )}
            >
              <Icon
                className={cn(
                  'h-[18px] w-[18px]',
                  selected ? 'text-primary-700' : 'text-neutral-500'
                )}
                weight={selected ? 'fill' : 'regular'}
              />
              <div
                className={cn(
                  'mt-1 font-semibold',
                  selected ? 'text-primary-700' : 'text-neutral-900'
                )}
              >
                {label}
              </div>
              <div className={cn('text-xs', selected ? 'text-primary-700/80' : 'text-neutral-500')}>
                {help}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex-none space-y-4">
        {sendType === 'Regular' ? (
          <p className="text-sm text-neutral-600">
            This campaign sends as soon as you confirm on the Review step.
          </p>
        ) : null}

        {sendType === 'Scheduled' ? (
          <div className="max-w-md space-y-1.5">
            <Label htmlFor="schedule-datetime">Date and time</Label>
            <Input
              id="schedule-datetime"
              type="datetime-local"
              className={cn('h-10', scheduleError ? 'border-destructive' : undefined)}
              value={scheduledLocal}
              min={minDatetimeLocalValue()}
              onChange={(event) => {
                const next = fromDatetimeLocalValue(event.target.value);
                if (!next) return;
                onScheduleDateTimeChange(next.date, next.time);
              }}
            />
            {scheduleError ? (
              <p className="text-xs text-destructive" role="alert">
                {scheduleError}
              </p>
            ) : (
              <p className="text-xs text-neutral-500">{timezoneHelp}</p>
            )}
            {timezoneSelect}
          </div>
        ) : null}

        {sendType === 'Recurring' ? (
          <div className="space-y-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="schedule-repeats">Repeats</Label>
                <Select
                  value={recurringFrequency}
                  onValueChange={(value) =>
                    onRecurringFrequencyChange(value as CampaignScheduleFrequency)
                  }
                >
                  <SelectTrigger id="schedule-repeats" className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRING_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schedule-starts">Starts</Label>
                <Input
                  id="schedule-starts"
                  type="datetime-local"
                  className={cn('h-10', scheduleError ? 'border-destructive' : undefined)}
                  value={recurringLocal}
                  min={minDatetimeLocalValue()}
                  onChange={(event) => {
                    const next = fromDatetimeLocalValue(event.target.value);
                    if (!next) return;
                    onRecurringStartDateChange(next.date);
                    onScheduleDateTimeChange(next.date, next.time);
                  }}
                />
                {scheduleError ? (
                  <p className="text-xs text-destructive" role="alert">
                    {scheduleError}
                  </p>
                ) : (
                  <p className="text-xs text-neutral-500">{timezoneHelp}</p>
                )}
              </div>
            </div>
            <div className="max-w-md">{timezoneSelect}</div>
            <p className="text-xs text-neutral-600">{recurrenceSummary}</p>
          </div>
        ) : null}
      </div>

      {audiencePreview || contentPreview ? (
        <div className="grid gap-3 border-t border-neutral-200 pt-4 md:grid-cols-2 md:gap-4">
          {audiencePreview ? (
            <div className="flex h-full flex-col gap-2 rounded-lg border border-neutral-200 bg-neutral-50/60 p-3">
              <div className="flex items-center gap-2">
                <UsersThree className="size-[18px] text-primary-600" aria-hidden />
                <h3 className="text-sm font-semibold text-neutral-900">Audience</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border border-neutral-200 bg-white px-3 py-2">
                  <span className="block text-xs text-neutral-500">Audience</span>
                  <span className="mt-0.5 block text-base font-semibold tabular-nums text-neutral-900">
                    {audiencePreview.audienceCount.toLocaleString()}
                  </span>
                </div>
                <div className="rounded-md border border-neutral-200 bg-white px-3 py-2">
                  <span className="block text-xs text-neutral-500">Sendable</span>
                  <span className="mt-0.5 block text-base font-semibold tabular-nums text-neutral-900">
                    {audiencePreview.sendableCount.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="space-y-1 text-[13px]">
                <p className="text-neutral-700">
                  <span className="font-medium text-neutral-900">Lists: </span>
                  {formatPreviewNames(audiencePreview.listNames, 'None selected')}
                </p>
                <p className="text-neutral-700">
                  <span className="font-medium text-neutral-900">Segments: </span>
                  {formatPreviewNames(audiencePreview.segmentNames, 'None selected')}
                </p>
              </div>
            </div>
          ) : null}

          {contentPreview ? (
            <div className="flex h-full flex-col gap-2 rounded-lg border border-neutral-200 bg-neutral-50/60 p-3">
              <h3 className="text-sm font-semibold text-neutral-900">Content</h3>
              {contentSrcDoc ? (
                <div className="flex h-[150px] w-[220px] flex-none flex-col overflow-hidden rounded border border-neutral-200 bg-white">
                  <div className="relative min-h-0 flex-1 overflow-hidden">
                    <iframe
                      title="Campaign content preview"
                      className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
                      style={{ width: 600, height: 420, transform: 'scale(0.36)' }}
                      sandbox=""
                      srcDoc={withStyleNonce(contentSrcDoc)}
                    />
                  </div>
                  <div className="h-3 flex-none bg-white" aria-hidden />
                </div>
              ) : (
                <div className="flex h-[150px] w-[220px] items-center justify-center rounded border border-dashed border-neutral-300 bg-white text-sm text-neutral-500">
                  No content yet
                </div>
              )}
              <p className="text-[13px] text-neutral-600">{contentPreview.label}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
